import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { UpdateReadingProgressDialog } from '@/components/UpdateReadingProgressDialog';

interface SelectedBook {
  id: string;
  title: string;
  authors: string[];
  cover_url: string | null;
}

interface UserBookDetails {
  user_book_id: string;
  current_page: number;
  total_pages: number;
}

interface FocusCelebrationProps {
  onClose: () => void;
  selectedBook?: SelectedBook | null;
  userBookDetails?: UserBookDetails | null;
  onProgressUpdated?: () => void;
}

export function FocusCelebration({ 
  onClose, 
  selectedBook, 
  userBookDetails,
  onProgressUpdated 
}: FocusCelebrationProps) {
  const [show, setShow] = useState(true);
  const [showProgressDialog, setShowProgressDialog] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShow(false);
      // Don't auto-close if we have a book to update
      if (!selectedBook) {
        setTimeout(onClose, 500);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose, selectedBook]);

  const handleUpdateProgress = () => {
    setShowProgressDialog(true);
  };

  const handleProgressDialogClose = () => {
    setShowProgressDialog(false);
    onClose();
  };

  const handleProgressUpdatedInternal = () => {
    onProgressUpdated?.();
    setShowProgressDialog(false);
    onClose();
  };

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm transition-opacity duration-500",
          show ? "opacity-100" : "opacity-0"
        )}
      >
        <div className="relative flex flex-col items-center gap-6 p-8 animate-scale-in">
          {/* Confetti books */}
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${2 + Math.random()}s`,
              }}
            >
              <div
                className="w-3 h-4 rounded-sm"
                style={{
                  backgroundColor: i % 3 === 0 
                    ? 'hsl(335, 42%, 40%)' 
                    : i % 3 === 1 
                    ? 'hsl(45, 100%, 76%)' 
                    : 'hsl(82, 60%, 34%)',
                  transform: `rotate(${Math.random() * 360}deg)`,
                }}
              />
            </div>
          ))}

          {/* Jumping Dino */}
          <div className="animate-bounce">
            <svg width="120" height="120" viewBox="0 0 200 200" fill="none">
              <ellipse cx="100" cy="120" rx="40" ry="45" fill="hsl(82, 60%, 34%)" />
              <circle cx="100" cy="75" r="30" fill="hsl(82, 60%, 34%)" />
              <circle cx="108" cy="70" r="5" fill="hsl(140, 35%, 17%)" />
              <circle cx="108" cy="70" r="10" fill="none" stroke="hsl(140, 35%, 17%)" strokeWidth="2" />
              <circle cx="92" cy="70" r="10" fill="none" stroke="hsl(140, 35%, 17%)" strokeWidth="2" />
              <line x1="102" y1="70" x2="98" y2="70" stroke="hsl(140, 35%, 17%)" strokeWidth="2" />
              <path d="M 85 85 Q 100 90 115 85" stroke="hsl(140, 35%, 17%)" strokeWidth="2" fill="none" />
              <rect x="85" y="100" width="8" height="12" fill="hsl(335, 42%, 40%)" rx="1" />
              <rect x="95" y="97" width="8" height="15" fill="hsl(45, 100%, 76%)" rx="1" />
              <rect x="105" y="100" width="8" height="12" fill="hsl(335, 42%, 40%)" rx="1" />
              <rect x="80" y="155" width="15" height="30" rx="8" fill="hsl(82, 60%, 34%)" />
              <rect x="105" y="155" width="15" height="30" rx="8" fill="hsl(82, 60%, 34%)" />
            </svg>
          </div>

          {/* Sparkles */}
          <div className="absolute top-20 left-1/4 animate-pulse">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <div className="absolute top-20 right-1/4 animate-pulse" style={{ animationDelay: '0.3s' }}>
            <Sparkles className="w-8 h-8 text-secondary" />
          </div>

          {/* Success message */}
          <div className="text-center space-y-4">
            <h2 className="text-4xl font-bold text-foreground font-heading">
              Great Reading Session! 🎉
            </h2>
            <p className="text-lg text-muted-foreground">
              You stayed focused and completed your session
            </p>

            <div className="flex gap-3 pt-4">
              {selectedBook && userBookDetails && (
                <Button onClick={handleUpdateProgress} className="btn-pill">
                  Update Reading Progress
                </Button>
              )}
              <Button onClick={onClose} variant="outline" className="btn-pill">
                Continue
              </Button>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes confetti {
            0% {
              transform: translateY(-100vh) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translateY(100vh) rotate(720deg);
              opacity: 0;
            }
          }
          .animate-confetti {
            animation: confetti 3s ease-in forwards;
          }
        `}</style>
      </div>

      {/* Progress Update Dialog */}
      {selectedBook && userBookDetails && (
        <UpdateReadingProgressDialog
          open={showProgressDialog}
          onOpenChange={(open) => {
            if (!open) handleProgressDialogClose();
          }}
          bookId={selectedBook.id}
          userBookId={userBookDetails.user_book_id}
          currentPage={userBookDetails.current_page}
          totalPages={userBookDetails.total_pages}
          bookTitle={selectedBook.title}
          onProgressUpdated={handleProgressUpdatedInternal}
        />
      )}
    </>
  );
}
